/**
 * FullscreenViewer — Swiper-powered invisible touch layer for fullscreen mode.
 * Sits on top of the regular Viewer to provide hardware-accelerated swipe
 * navigation between shots. The visual content is rendered by Viewer underneath;
 * this component provides the swipe physics and slide previews.
 *
 * When the user swipes, Swiper shows the adjacent shot images and triggers
 * selectShot + seek when the slide settles. The regular Viewer then re-renders
 * with the new shot data (including overlays, drawing mode, etc.).
 */

import { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Virtual } from 'swiper/modules';
import type SwiperType from 'swiper';
import { diagLog } from '@/bridge/embeddedBridge.ts';
import { useVideoSync } from '@/hooks/useVideoSync.ts';
import { useShotFullImage } from '@/hooks/useShotFullImage.ts';
import { getCachedAssetBytes, readAssetBytes, toArrayBuffer } from '@/previs/assetStorage.ts';
import 'swiper/css';
import 'swiper/css/virtual';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import styles from './FullscreenViewer.module.css';

const TAG = 'FullscreenViewer';

export function FullscreenViewer() {
  const project = useProjectStore(s => s.project);
  const currentTime = usePlaybackStore(s => s.currentTime);
  const getShotAtTime = useProjectStore(s => s.getShotAtTime);
  const drawingMode = useUiStore(s => s.drawingMode);
  const swiperRef = useRef<SwiperType | null>(null);
  const isSwiperNav = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Build flat shot list with start times
  const shotList = useMemo(() => {
    if (!project) return [];
    const list: Array<{ shot: typeof project.scenes[0]['shots'][0]; startTime: number }> = [];
    for (const scene of project.scenes) {
      if (scene.isOmitted) continue;
      for (const shot of scene.shots) {
        const prev = list[list.length - 1];
        const startTime = prev ? prev.startTime + prev.shot.duration : 0;
        list.push({ shot, startTime });
      }
    }
    return list;
  }, [project]);

  // Current shot index
  const currentShot = getShotAtTime(currentTime);
  const currentIdx = currentShot ? shotList.findIndex(s => s.shot.id === currentShot.id) : 0;
  const currentEntry = currentIdx >= 0 ? shotList[currentIdx] : null;

  // Full-res static image for the ACTIVE slide only — fullscreen is the largest render target in
  // the app, so it is where the cache thumbnail's softness shows most. Deliberately not per-slide:
  // one hook call means one request in flight, and a fast swipe walks it across slides one at a
  // time instead of fanning out a request (and a decoded full-res image) for every neighbour.
  const activeFullResUrl = useShotFullImage(currentEntry?.shot ?? null);

  // Sync video playback for the active shot. Try the byte cache first, then fall
  // back to fetching the file:// bytes — mirrors Viewer's readAssetBytes fallback
  // (Viewer.tsx). Without the fallback, fullscreen video is silently empty on a
  // cache miss (e.g. opening a shot straight into fullscreen before the main
  // Viewer has cached its bytes). The cancel guard drops a late fetch when the
  // user swipes to another shot mid-load.
  const [activeVideoData, setActiveVideoData] = useState<ArrayBuffer | null>(null);
  const activeVideoUrl = currentEntry?.shot.videoObjectUrl;
  useEffect(() => {
    if (!activeVideoUrl) { setActiveVideoData(null); return; }
    const cached = getCachedAssetBytes(activeVideoUrl);
    if (cached) { setActiveVideoData(toArrayBuffer(cached)); return; }
    let cancelled = false;
    setActiveVideoData(null);
    readAssetBytes(activeVideoUrl).then(bytes => {
      if (!cancelled && bytes) setActiveVideoData(toArrayBuffer(bytes));
    });
    return () => { cancelled = true; };
  }, [activeVideoUrl]);
  useVideoSync(canvasRef, activeVideoData, currentEntry?.startTime ?? 0, currentEntry?.shot.duration ?? 0);

  // Diagnostic logging on mount
  useEffect(() => {
    const totalBytes = shotList.reduce((sum, s) => sum + (s.shot.thumbnailDataUrl?.length ?? 0), 0);
    diagLog(TAG, `MOUNT — ${shotList.length} shots, currentIdx=${currentIdx}, totalUrlChars=${totalBytes.toLocaleString()}`);
    return () => {
      diagLog(TAG, 'UNMOUNT');
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync Swiper position when timeline changes externally (playback, click)
  useEffect(() => {
    if (swiperRef.current && !isSwiperNav.current && currentIdx >= 0) {
      if (swiperRef.current.activeIndex !== currentIdx) {
        swiperRef.current.slideTo(currentIdx, 0);
      }
    }
    isSwiperNav.current = false;
  }, [currentIdx]);

  // Disable touch when drawing
  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.allowTouchMove = !drawingMode;
    }
  }, [drawingMode]);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    const entry = shotList[swiper.activeIndex];
    if (!entry) return;
    diagLog(TAG, `Slide changed to idx=${swiper.activeIndex}, DOM slides=${swiper.slides?.length ?? '?'}, virtual=${swiper.virtual?.slides?.length ?? 'N/A'}`);
    isSwiperNav.current = true;
    useProjectStore.getState().selectShot(entry.shot.id);
    usePlaybackStore.getState().seek(entry.startTime);
  }, [shotList]);

  if (shotList.length === 0) return null;

  return (
    <div className={styles.touchLayer}>
      <Swiper
        modules={[Virtual]}
        virtual
        initialSlide={Math.max(0, currentIdx)}
        slidesPerView={1}
        spaceBetween={0}
        speed={400}
        onSwiper={swiper => {
          swiperRef.current = swiper;
          diagLog(TAG, `Swiper init — DOM slides=${swiper.slides?.length ?? '?'}, virtual=${swiper.virtual?.slides?.length ?? 'N/A'}, virtualEnabled=${!!swiper.params?.virtual}`);
        }}
        onSlideChange={handleSlideChange}
        className={styles.swiper}
      >
        {shotList.map(({ shot }, i) => {
          const isActive = i === currentIdx;
          const hasVideo = !!shot.videoObjectUrl;
          const mediaUrl = (isActive ? activeFullResUrl : null) ?? shot.thumbnailDataUrl;
          return (
            <SwiperSlide key={shot.id} virtualIndex={i} className={styles.slide}>
              {hasVideo ? (
                isActive ? (
                  <canvas ref={canvasRef} className={styles.mediaCanvas} />
                ) : (
                  mediaUrl ? <img className={styles.media} src={mediaUrl} alt="" /> : null
                )
              ) : mediaUrl ? (
                <img className={styles.media} src={mediaUrl} alt="" />
              ) : (
                <div className={styles.placeholder}>{shot.number || '?'}</div>
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}
